import {
	addDays,
	addHours,
	getDay,
	setHours,
	setMinutes,
	setSeconds,
	setMilliseconds,
	isSameDay,
	parseISO,
	isBefore,
	isAfter,
} from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

import { publicHolidays } from '../utils/config';

export const SYDNEY_TZ = 'Australia/Sydney';

export interface AssessmentTimeConfig {
	earliestHour: number;
	earliestMinute: number;
	latestHour: number;
	latestMinute: number;
	timeStepMinutes: number;
	leadHours: number;
}

export const preferredAssessmentTimeConfig: AssessmentTimeConfig = {
	earliestHour: 8,
	earliestMinute: 30,
	latestHour: 16,
	latestMinute: 30,
	timeStepMinutes: 30,
	leadHours: 4,
};

export const assessmentTimeConfig: AssessmentTimeConfig = {
	earliestHour: 7,
	earliestMinute: 0,
	latestHour: 18,
	latestMinute: 0,
	timeStepMinutes: 15,
	leadHours: 0,
};

const setDateTime = (date: Date, hours: number, minutes: number): Date => {
	return setMilliseconds(setSeconds(setMinutes(setHours(date, hours), minutes), 0), 0);
};

const roundUpToTimeStep = (date: Date, stepMinutes: number): Date => {
	const minutes = date.getMinutes();
	const remainder = minutes % stepMinutes;
	const roundedMinutes = remainder === 0 ? minutes : minutes + (stepMinutes - remainder);
	const adjusted = setMinutes(date, roundedMinutes);

	return setMilliseconds(setSeconds(adjusted, 0), 0);
};

const isPreferredAssessmentHoliday = (date: Date): boolean => {
	return publicHolidays.some((holiday) => isSameDay(date, parseISO(holiday)));
};

const isPreferredAssessmentWeekend = (date: Date): boolean => {
	const dayOfWeek = getDay(date);

	return dayOfWeek === 0 || dayOfWeek === 6;
};

export const getPreferredAssessmentEarliestTime = (
	date: Date,
	now: Date = new Date(),
	config: AssessmentTimeConfig = preferredAssessmentTimeConfig,
): Date => {
	const earliest = setDateTime(date, config.earliestHour, config.earliestMinute);

	if (!isSameDay(date, now)) {
		return earliest;
	}

	const leadTime = roundUpToTimeStep(addHours(now, config.leadHours), config.timeStepMinutes);

	return isBefore(leadTime, earliest) ? earliest : leadTime;
};

export const getPreferredAssessmentLatestTime = (
	date: Date,
	config: AssessmentTimeConfig = preferredAssessmentTimeConfig,
): Date => {
	return setDateTime(date, config.latestHour, config.latestMinute);
};

export const isPreferredAssessmentDateDisabled = (
	date: Date,
	now: Date = new Date(),
	config: AssessmentTimeConfig = preferredAssessmentTimeConfig,
): boolean => {
	if (isPreferredAssessmentWeekend(date) || isPreferredAssessmentHoliday(date)) {
		return true;
	}

	if (isSameDay(date, now)) {
		const earliest = getPreferredAssessmentEarliestTime(date, now, config);
		const latest = getPreferredAssessmentLatestTime(date, config);

		return isAfter(earliest, latest);
	}

	// Disable past days. `date`/`now` must be in the same "wall clock" space (e.g. both
	// converted to Sydney time) for this to correctly reflect Sydney's calendar day.
	return isBefore(date, now);
};

export const isPreferredAssessmentTimeAllowed = (
	date: Date,
	now: Date = new Date(),
	config: AssessmentTimeConfig = preferredAssessmentTimeConfig,
): boolean => {
	if (isPreferredAssessmentDateDisabled(date, now, config)) {
		return false;
	}

	const earliest = getPreferredAssessmentEarliestTime(date, now, config);
	const latest = getPreferredAssessmentLatestTime(date, config);

	if (isBefore(date, earliest) || isAfter(date, latest)) {
		return false;
	}

	return date.getMinutes() % config.timeStepMinutes === 0;
};

// Operates entirely on Sydney wall-clock (pseudo-local) dates, so weekday/holiday
// checks are always evaluated against Sydney's calendar, not the caller's local one.
const calculateNextValidSydneyDate = (sydneyDate: Date, config: AssessmentTimeConfig): Date => {
	const dayOfWeek = getDay(sydneyDate);
	let daysToAdd = 1;

	// If it's Friday (5), add 3 days to get to Monday
	if (dayOfWeek === 5) {
		daysToAdd = 3;
	}
	// If it's Saturday (6), add 2 day to get to Monday
	else if (dayOfWeek === 6) {
		daysToAdd = 2;
	}

	const nextDate = addDays(sydneyDate, daysToAdd);

	const isPublicHoliday = isPreferredAssessmentHoliday(nextDate);

	if (isPublicHoliday) {
		return calculateNextValidSydneyDate(nextDate, config);
	}

	// default to the earliest available assessment time
	return setDateTime(nextDate, config.earliestHour, config.earliestMinute);
};

// `date` and the return value are real instants (e.g. `new Date()`, or a UTC timestamp
// from the API) - the Sydney conversion happens internally so this is correct regardless
// of the caller's own timezone.
export const calculateNextValidDate = (
	date: Date,
	config: AssessmentTimeConfig = preferredAssessmentTimeConfig,
): Date => {
	const sydneyDate = utcToZonedTime(date, SYDNEY_TZ);
	const nextValidSydneyDate = calculateNextValidSydneyDate(sydneyDate, config);

	return zonedTimeToUtc(nextValidSydneyDate, SYDNEY_TZ);
};
